import { noop } from 'lodash';
import { createContext, memo, type ProviderProps, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type ScrollElementContextType = {
  scrollElement: HTMLElement | HTMLDivElement | null;
  setScrollElement: (scrollElement: HTMLElement | HTMLDivElement | null) => void;
};

export const initialScrollElementContext: ScrollElementContextType = {
  scrollElement: null,
  setScrollElement: noop,
};

export const ScrollElementContext = createContext<ScrollElementContextType>(initialScrollElementContext);

export type ScrollElementContextProviderProps = Pick<ProviderProps<ScrollElementContextType>, 'children'>;

export const ScrollElementContextProvider = memo(({ children }: ScrollElementContextProviderProps) => {
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  const value = useMemo(() => ({ scrollElement, setScrollElement }), [scrollElement]);

  return <ScrollElementContext.Provider value={value}>{children}</ScrollElementContext.Provider>;
});

ScrollElementContextProvider.displayName = 'ScrollElementContextProvider';

export const ScrollElementContextConsumer = ScrollElementContext.Consumer;

export const useScrollElementContext = () => {
  const context = useContext(ScrollElementContext);

  if (context === initialScrollElementContext) {
    const error = 'useScrollElementContext must be used within a ScrollElementContextProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
